import { Button, EmptyState, Flex, Grid, Icon, Label, Text } from '@pega/cosmos-react-core';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import useLocalTranslation from '../../../../hooks/useLocalTranslation';
import { updateLoaderDetails } from '../../../../redux/actions';
import { convertDate } from '../../../../utils/helpers';
import { InteractiveText } from '../../../Utilities/custom.styled';
import { getSimulationDetails } from '../AISimulatorApis';
import SimulationMetricsDetails from './SimulationMetricsDetails';

export interface SimulationMetricsProps {
  connectionId: string;
  sessionId: string;
}

const cardStyle = {
  width: '95%',
  height: '6.4rem',
  border: '0.06rem solid #D8D8D8',
};

const gridWidth = { width: '100%' };

const flexPadding = {
  paddingLeft: '1rem',
  paddingTop: '1rem',
};

const excludingKeys = new Set([
  'previous_address',
  'last_name',
  'first_name',
  'marital_status',
  'salutation',
  'isEnabled',
  'gender',
  'social_security_number',
  'messages',
]);

const SimulationMetrics: React.FC<SimulationMetricsProps> = ({ connectionId, sessionId }) => {
  const translate = useLocalTranslation();
  const dispatch = useDispatch();
  const [simulationRecords, setSimulationRecords] = useState<any[]>([]);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [selectedSimulationRecord, setSelectedSimulationRecord] = useState<any[]>([]);
  const [csvFile, setCsvFile] = useState('');

  const fetchSimulationDetails = useCallback(async () => {
    dispatch(updateLoaderDetails(true));
    let response = await getSimulationDetails(sessionId, connectionId);
    dispatch(updateLoaderDetails(false));
    if (response?.status === 200) {
      const responseData = response?.data;
      const simulationData = Object.values(responseData);
      const sortedSimulationData = simulationData.sort((a: any, b: any) =>
        new Date(b.startTime) > new Date(a.startTime) ? 1 : -1,
      );
      setSimulationRecords(sortedSimulationData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, sessionId, connectionId]);

  useEffect(() => {
    fetchSimulationDetails();
  }, [fetchSimulationDetails]);

  const onShowMoreClick = (id: string) => {
    const simulationData = simulationRecords.find((simulationRecord) => id === simulationRecord?.simulationId);
    setSelectedSimulationRecord(simulationData);
    setShowCustomerDetails(true);
  };

  const flattenCustomerObject = (customerData: any = {}, updatedCustomerData: any = {}, extraKey = '') => {
    for (const key in customerData) {
      if (!excludingKeys.has(key)) {
        if (typeof customerData[key] !== 'object' || Array.isArray(customerData[key])) {
          const updatedData = ['expiresAt', 'stopTime', 'start_time'].includes(key)
            ? convertDate(customerData[key])
            : customerData[key];

          updatedCustomerData[extraKey + key] = updatedData;
        } else {
          flattenCustomerObject(customerData[key], updatedCustomerData, `${extraKey}${key}.`);
        }
      }
    }
    return updatedCustomerData;
  };

  const createCsv = (id: string) => {
    const simulationData = simulationRecords.find((simulationRecord) => id === simulationRecord?.simulationId);

    if (simulationData) {
      const headers = ['simulationId', 'startTime', simulationData?.stopTime && 'stopTime'];
      const simulationValues = [
        simulationData?.simulationId,
        convertDate(simulationData?.startTime),
        convertDate(simulationData?.stopTime),
      ];
      const customers = simulationData?.customers?.map((customer: any) => flattenCustomerObject(customer));

      const maxCustomerHeader = customers?.reduce((customer1: any, customer2: any) => {
        let customerKeys1 = customer1;

        if (customer1?.length === undefined) {
          customerKeys1 = Object.keys(customer1);
        }
        const customerKeys2 = Object.keys(customer2);
        return customerKeys1?.length > customerKeys2?.length ? customerKeys1 : customerKeys2;
      });

      const replacer = (key: any, value: any) => (value === null ? '' : value);
      const customerHeaders = maxCustomerHeader?.length ? maxCustomerHeader : Object.keys(customers[0]);
      const csv = [
        headers.join(','),
        simulationValues.join(','),
        '\nCustomer Details\n',
        customerHeaders.join(','),
        ...customers.map((row: { [x: string]: any }) =>
          customerHeaders
            ?.map((fieldName: string | number) => JSON.stringify(row[fieldName], replacer)?.replaceAll('\\n', ' '))
            ?.join(','),
        ),
      ].join('\r\n');
      setCsvFile(csv);
    }
  };

  return (
    <>
      {showCustomerDetails ? (
        <SimulationMetricsDetails
          simulationData={selectedSimulationRecord}
          setShowCustomerDetails={setShowCustomerDetails}
          csvFile={csvFile}
          createCsv={createCsv}
        />
      ) : (
        <>
          {simulationRecords?.length ? (
            simulationRecords.map((item: any, index) => {
              return (
                <Grid
                  container={{
                    cols: 'repeat(1, 90% 10%)',
                    gap: 1,
                    alignItems: 'center',
                  }}
                  style={cardStyle}
                  key={index}
                >
                  <Flex container={{ direction: 'column', itemGap: 1, alignItems: 'center' }} style={flexPadding}>
                    {item.startTime && (
                      <Grid
                        container={{
                          cols: 'repeat(1, 65% 45%)',
                          gap: 1,
                          alignItems: 'center',
                        }}
                        style={gridWidth}
                      >
                        <Label>{translate('start_time')}</Label>
                        <Text variant="secondary">{convertDate(item.startTime)}</Text>
                      </Grid>
                    )}
                    {item.stopTime && (
                      <Grid
                        container={{
                          cols: 'repeat(1, 65% 45%)',
                          gap: 1,
                          alignItems: 'center',
                        }}
                        style={gridWidth}
                      >
                        <Label>{translate('stop_time')}</Label>
                        <Text variant="secondary">{convertDate(item.stopTime)}</Text>
                      </Grid>
                    )}
                    <InteractiveText
                      className="pointer"
                      onClick={() => {
                        onShowMoreClick(item.simulationId);
                      }}
                      style={{ marginBottom: 1, marginLeft: '3rem' }}
                    >
                      {translate('show_messages')}
                    </InteractiveText>
                  </Flex>
                  <Flex container={{ direction: 'row', itemGap: 1, alignItems: 'center' }} style={{ height: '100%' }}>
                    <Button
                      href={`data:text/csv;charset=utf-8,${csvFile}`}
                      download={`Simulation-history-${item.simulationId}.csv`}
                      icon
                      style={{ height: 15, width: 15 }}
                      label="Download data in csv file"
                      onClick={() => {
                        createCsv(item.simulationId);
                      }}
                    >
                      <Icon name="download" />
                    </Button>
                  </Flex>
                </Grid>
              );
            })
          ) : (
            <EmptyState message={translate('empty_simulation_history')} />
          )}
        </>
      )}
    </>
  );
};

export default SimulationMetrics;
